import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { EnrollTwoFactor } from "@/components/EnrollTwoFactor";
import { LogOut, LayoutDashboard, Heart, Gift, Repeat, FileText, Megaphone, Sparkles, MessageSquare, Settings, ShieldCheck, Lock, Clock } from "lucide-react";

const SECTIONS = [
  { id: "giving", label: "My Giving", icon: Gift },
  { id: "history", label: "Donation History", icon: Clock },
  { id: "recurring", label: "Recurring Donations", icon: Repeat },
  { id: "receipts", label: "Receipts", icon: FileText },
  { id: "campaigns", label: "Campaigns I Supported", icon: Megaphone },
  { id: "impact", label: "Impact Updates", icon: Sparkles },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "settings", label: "Account Settings", icon: Settings },
  { id: "security", label: "Security", icon: ShieldCheck },
];

const GIVING_NOTE = "Your Refugium Ethiopia giving history will appear here when direct donations become available on our website. Donations currently made through GoFundMe are processed outside of Refugium Ethiopia and will not automatically appear here.";

function ComingSoon({ title }) {
  return (
    <div className="border border-dashed border-[#1F4D2E]/25 rounded-xl p-10 text-center bg-[#1F4D2E]/[0.03]" data-testid="giving-empty">
      <span className="w-14 h-14 rounded-full bg-[#E0A526]/20 text-[#B45B3E] flex items-center justify-center mx-auto mb-4"><Lock size={24} /></span>
      <h3 className="font-display text-xl font-bold text-[#1F4D2E] mb-2">{title}</h3>
      <p className="text-sm text-[#4F6B54] max-w-md mx-auto">{GIVING_NOTE}</p>
      <a href="https://www.gofundme.com" target="_blank" rel="noreferrer" className="inline-block mt-5 bg-[#C1272D] text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-[#9E1F24] transition-colors">Give via GoFundMe</a>
    </div>
  );
}

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const [tab, setTab] = useState("giving");
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/donor/overview").then(({ data }) => setData(data)).catch(() => {});
    api.get("/donor/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); nav("/"); };
  const reload = () => api.get("/donor/overview").then(({ data }) => setData(data)).catch(() => {});

  return (
    <div className="noise-bg min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid="account-page">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-2">Donor dashboard</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1F4D2E]">Welcome, {user?.name || "friend"}.</h1>
            <p className="text-[#4F6B54] mt-2" data-testid="account-email">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {["super_admin", "employee"].includes(user?.role) && (
              <Link to="/admin" data-testid="account-admin-link" className="inline-flex items-center gap-2 border border-[#1F4D2E] text-[#1F4D2E] rounded-full px-5 py-2.5 font-semibold hover:bg-[#1F4D2E] hover:text-white transition-colors"><LayoutDashboard size={16} /> Admin</Link>
            )}
            <button onClick={doLogout} data-testid="account-logout" className="inline-flex items-center gap-2 bg-[#1F4D2E] text-white rounded-full px-5 py-2.5 font-semibold hover:bg-[#C1272D] transition-colors"><LogOut size={16} /> Log out</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-8">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto hide-scrollbar" data-testid="donor-nav">
            {SECTIONS.map((s) => (
              <button key={s.id} onClick={() => setTab(s.id)} data-testid={`donor-tab-${s.id}`}
                className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap text-left transition-colors ${tab === s.id ? "bg-[#1F4D2E] text-white" : "text-[#1F4D2E] hover:bg-[#1F4D2E]/10"}`}>
                <s.icon size={16} /> {s.label}
              </button>
            ))}
          </nav>

          <section className="min-w-0" data-testid={`donor-panel-${tab}`}>
            {tab === "giving" && (
              <div>
                <h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">My Giving</h2>
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                  {[["Total given", "—"], ["Donations", "0"], ["Active recurring", "0"]].map(([l, v]) => (
                    <div key={l} className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5"><p className="text-xs uppercase tracking-wide text-[#4F6B54]">{l}</p><p className="font-display text-2xl font-bold text-[#1F4D2E] mt-1">{v}</p></div>
                  ))}
                </div>
                <ComingSoon title="On-site giving is coming soon" />
              </div>
            )}
            {tab === "history" && <div><h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Donation History</h2><ComingSoon title="No donation history yet" /></div>}
            {tab === "recurring" && <div><h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Recurring Donations</h2><ComingSoon title="No recurring gifts yet" /></div>}
            {tab === "receipts" && <div><h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Receipts</h2><ComingSoon title="No receipts yet" /></div>}
            {tab === "campaigns" && <div><h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Campaigns I Supported</h2><ComingSoon title="No campaigns yet" /></div>}
            {tab === "impact" && (
              <div>
                <h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Impact Updates</h2>
                <div className="border border-dashed border-[#1F4D2E]/25 rounded-xl p-10 text-center" data-testid="impact-empty">
                  <Sparkles className="mx-auto text-[#E0A526] mb-3" size={28} />
                  <p className="text-sm text-[#4F6B54] max-w-md mx-auto">We're just getting started. As we build our first safe home, campaign and impact updates will appear here. Turn on impact updates in Account Settings to be notified.</p>
                </div>
              </div>
            )}
            {tab === "messages" && <MessagesPanel messages={messages} onSent={() => api.get("/donor/messages").then(({ data }) => setMessages(data))} />}
            {tab === "settings" && data && <SettingsPanel data={data} onSaved={() => { reload(); refresh(); }} />}
            {tab === "security" && data && <SecurityPanel mfaEnabled={data.mfa_enabled} onChange={() => { reload(); refresh(); }} />}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function MessagesPanel({ messages, onSent }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/donor/messages", { subject, message }); toast.success("Message sent to our team"); setSubject(""); setMessage(""); onSent(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Messages</h2>
      <form onSubmit={send} className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5 mb-6">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="donor-msg-subject" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm mb-3" />
        <textarea required value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="How can we help?" data-testid="donor-msg-body" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm mb-3" />
        <button disabled={busy} data-testid="donor-msg-send" className="bg-[#1F4D2E] text-white rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-50">Contact support</button>
      </form>
      <div className="space-y-3" data-testid="donor-msg-list">
        {messages.length === 0 ? <p className="text-sm text-[#4F6B54]">No messages yet.</p> : messages.map((m) => (
          <div key={m.id} className="bg-white border border-[#1F4D2E]/10 rounded-lg p-4">
            <div className="flex justify-between text-sm"><span className="font-semibold text-[#1F4D2E]">{m.subject || "Support request"}</span><span className="text-xs px-2 py-0.5 rounded-full bg-[#1F4D2E]/10 text-[#1F4D2E]">{m.status}</span></div>
            <p className="text-sm text-[#4F6B54] mt-1">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel({ data, onSaved }) {
  const [p, setP] = useState(data.profile);
  const [prefs, setPrefs] = useState(data.preferences);
  const saveProfile = async (e) => { e.preventDefault(); try { await api.put("/donor/profile", p); toast.success("Profile saved"); onSaved(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const savePrefs = async () => { try { await api.put("/donor/preferences", prefs); toast.success("Preferences saved"); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const cls = "w-full border border-gray-200 rounded-md px-3 py-2 text-sm";
  const Check = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm text-[#1F4D2E]"><input type="checkbox" checked={!!prefs[k]} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })} data-testid={`pref-${k}`} /> {label}</label>
  );
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Account Settings</h2>
      <form onSubmit={saveProfile} className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-[#1F4D2E] mb-3">Contact information</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder="Full name" data-testid="profile-name" className={cls} />
          <input value={p.phone || ""} onChange={(e) => setP({ ...p, phone: e.target.value })} placeholder="Phone / WhatsApp" data-testid="profile-phone" className={cls} />
          <input value={p.address || ""} onChange={(e) => setP({ ...p, address: e.target.value })} placeholder="Address" data-testid="profile-address" className={cls} />
          <input value={p.city || ""} onChange={(e) => setP({ ...p, city: e.target.value })} placeholder="City" data-testid="profile-city" className={cls} />
          <input value={p.country || ""} onChange={(e) => setP({ ...p, country: e.target.value })} placeholder="Country" data-testid="profile-country" className={cls} />
        </div>
        <button data-testid="save-profile" className="mt-4 bg-[#1F4D2E] text-white rounded-full px-6 py-2.5 text-sm font-semibold">Save profile</button>
      </form>
      <div className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5">
        <h3 className="font-semibold text-[#1F4D2E] mb-3">Communication preferences</h3>
        <div className="space-y-2.5">
          <Check k="newsletter" label="Newsletter" />
          <Check k="campaign_updates" label="Campaign updates" />
          <Check k="impact_updates" label="Impact updates" />
          <Check k="volunteer_interest" label="I'm interested in volunteering" />
        </div>
        <button onClick={savePrefs} data-testid="save-prefs" className="mt-4 bg-[#1F4D2E] text-white rounded-full px-6 py-2.5 text-sm font-semibold">Save preferences</button>
      </div>
    </div>
  );
}

function SecurityPanel({ mfaEnabled, onChange }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState(""); const [nw, setNw] = useState("");
  const [enroll, setEnroll] = useState(false);
  const [pw, setPw] = useState("");
  const changePw = async (e) => { e.preventDefault(); try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password updated. Please sign in again."); await logout(); navigate("/login"); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const disable = async () => { try { await api.post("/auth/2fa/disable", { password: pw }); toast.success("Two-factor disabled"); setPw(""); onChange(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const cls = "w-full border border-gray-200 rounded-md px-3 py-2 text-sm";
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#1F4D2E] mb-4">Security</h2>
      <form onSubmit={changePw} className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-[#1F4D2E] mb-3">Change password</h3>
        <input required type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" data-testid="cur-password" className={`${cls} mb-3`} />
        <input required type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (min 8 chars)" data-testid="new-password" className={`${cls} mb-3`} />
        <button data-testid="save-password" className="bg-[#1F4D2E] text-white rounded-full px-6 py-2.5 text-sm font-semibold">Update password</button>
      </form>
      <div className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5">
        <h3 className="font-semibold text-[#1F4D2E] mb-1">Two-factor authentication <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${mfaEnabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`} data-testid="mfa-status">{mfaEnabled ? "On" : "Off"}</span></h3>
        <p className="text-sm text-[#4F6B54] mb-4">Add an authenticator app for an extra layer of protection. Optional for donors.</p>
        {mfaEnabled ? (
          <div className="flex flex-wrap gap-2 items-center">
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Confirm password" data-testid="disable-2fa-pw" className={`${cls} max-w-xs`} />
            <button onClick={disable} data-testid="disable-2fa" className="border border-red-200 text-red-600 rounded-full px-5 py-2 text-sm font-semibold">Disable 2FA</button>
          </div>
        ) : enroll ? (
          <div className="max-w-md"><EnrollTwoFactor onComplete={() => { setEnroll(false); onChange(); toast.success("Two-factor enabled"); }} /></div>
        ) : (
          <button onClick={() => setEnroll(true)} data-testid="enable-2fa" className="bg-[#1F4D2E] text-white rounded-full px-6 py-2.5 text-sm font-semibold">Enable 2FA</button>
        )}
      </div>
    </div>
  );
}
